<template>
    <div v-cloak class="image-gallery">
        <div class="files same-height-tiles" id="files">
            <div class="file tile" v-for="file in sortedImages" :key="fileId(file)" :data-id="fileId(file)" :style="`background-image: url(${fileUrl(file)});`">
                <button class="remove" type="button" @click.prevent="removeFile(file)">
                    <fa-layers>
                        <fa-icon :icon="['fas', 'circle']" class="text-light" transform="grow-3" />
                        <fa-icon :icon="['fas', 'times-circle']" />
                    </fa-layers>
                </button>
                <div v-if="fileProgress(file) > 0 && fileProgress(file) < 100" class="file-progress"><div class="bar" :style="`width: ${fileProgress(file)}%;`"></div></div>
                <span v-if="fileError(file)">{{fileError(file)}}</span>
            </div>
        </div>
        
        <div class="text-center">
            <b>Drop images anywhere to upload or </b>
            
            <FileUpload
                class="btn btn-primary"
                style="margin-left: 3px;"
                :multiple="true"
                :drop="true"
                :drop-directory="true"
                v-model="images"
                ref="upload"
                @input-filter="inputFilter"
                @input-file="inputFile"
            >
                    <i class="far fa-plus"></i>
                    Browse
            </FileUpload>
        </div>
        
        <div v-if="uploadsInProgress" class="upload-in-progress">
            <i class="far fa-spinner fa-spin fa-fw"></i> <small><b>Uploading Images ...</b></small>
        </div>
        
        <div v-show="$refs.upload && $refs.upload.dropActive" class="drop-active" ref="dropActive">
            <h3>Drop images to upload</h3>
        </div>
    </div>
</template>

<script>
// https://medium.com/@shresthshruti09/uploading-files-in-aws-s3-bucket-through-javascript-sdk-with-progress-bar-d2a4b3ee77b5
import AWS from 'aws-sdk'
import QueueService from '@/services/queue.service'
import FileUpload from 'vue-upload-component'
import Sortable from 'sortablejs';

var bucketName = process.env.VUE_APP_BUCKET_NAME;
var bucketRegion = process.env.VUE_APP_BUCKET_REGION;
var identityPoolId = process.env.VUE_APP_IDENTITY_POOL_ID;

AWS.config.update({
    region: bucketRegion,
    credentials: new AWS.CognitoIdentityCredentials({
        IdentityPoolId: identityPoolId
    })
});

var s3 = new AWS.S3({
    apiVersion: '2006-03-01',
    params: {Bucket: bucketName}
});

export default {
    name: 'ImageGallery',
    components: {
        FileUpload,
    },
    props: {
        existingImages: {
            type: Array,
            default() { return [] },
        },
        useAws: {
            type: Boolean,
            default() { return false },
        },
    },
    data() {
        return {
            images: [],
            s3Queue: null,
        }
    },
    computed: {
        combinedImages() {
            return [...(this.existingImages || []), ...this.images]
        },
        sortedImages: {
            get() {
                return [...this.combinedImages].sort((a,b) => {
                    if (a.sort_order == b.sort_order) return 0;
                    return a.sort_order < b.sort_order ? -1 : 1;
                });
            },
            set(value) {
                console.log(value);
            },
        },
        imageData() {
            return this.sortedImages.map(file => {
                return this.isExisting(file) ? file : {...file.response.data, sort_order: file.sort_order};
            });
        },
        uploadsInProgress() {
            return this.images.filter(file => file.success == false && file.progress > 0).length > 0;
        },
        uploadsCompleted() {
            return this.images.filter(file => file.success == false).length <= 0;
        },
        totalUploadProgress() {
            if (this.uploadsCompleted) return 100;
            let totalToUpload = this.images.filter(file => file.success == false).length * 100;
            let totalUploaded = this.images.reduce((acc, file) => acc + parseFloat(file.progress));
            return totalUploaded / totalToUpload;
        },
    },
    watch: {
        existingImages() {
            this.cleanSortOrder()
        },
        images() {
            this.cleanSortOrder()
        },
    },
    methods: {
        startUpload() {
            if (this.images.length && !this.useAws) {
                this.$refs.upload.active = true;
            } else if (this.images.length && this.useAws) {
                this.s3Queue = new QueueService()
                this.images.forEach(image => this.s3Queue.enqueue(image))
                this.uploadToS3()
            } else {
                this.$emit('upload-complete', this.imageData);
            }
        },
        clearUploads() {
            this.$refs.upload.clear();
        },
        updateSort(/*event*/) {
            // this.list.splice(event.newIndex, 0, this.list.splice(event.oldIndex, 1)[0])
        },
        inputFile(newFile, oldFile) {
            if (newFile && !oldFile) { // Add file
                this.$refs.upload.update(newFile, {sort_order: this.combinedImages.length})
            } else if (newFile && oldFile) { // Update file (only used if if using built-in uploader)
                // Start upload
                if (newFile.active !== oldFile.active) {
                    console.log('Start upload', newFile.active, newFile)
                }
                
                // Upload progress
                if (newFile.progress !== oldFile.progress) {
                    console.log('progress', newFile.progress, newFile)
                }
                
                // Upload error
                if (newFile.error !== oldFile.error) {
                    console.log('error', newFile.error, newFile)
                }
                
                // Uploaded successfully
                if (newFile.success !== oldFile.success) {
                    console.log('success', newFile.success, newFile)
                    console.log('success', newFile.response, newFile)
                    
                    let response = newFile.response;
                    response.data.sort_order = newFile.sort_order;
                    this.$refs.upload.update(newFile, {response: response})
                    
                    if (this.uploadsCompleted) {
                        this.$emit('upload-complete', this.imageData);
                    }
                }
            } else if (!newFile && oldFile) { // Remove file
            }
        },
        inputFilter(newFile, oldFile, prevent) {
            if (newFile && !oldFile) {
                if (!/\.(jpg|jpeg|png)$/i.test(newFile.name)) {
                    alert('Please upload a JPEG or PNG image.')
                    return prevent()
                }
            }
            if (newFile && (!oldFile || newFile.file !== oldFile.file)) {
                newFile.url = ''
                let URL = window.URL || window.webkitURL
                if (URL && URL.createObjectURL) {
                    newFile.url = URL.createObjectURL(newFile.file)
                }
            }
        },
        isExisting(file) {
            return !isNaN(file.id);
        },
        fileId(file) {
            return file.id
        },
        fileUrl(file) {
            if (this.isExisting(file)) {
                return file.permalink;
            } else {
                return file.url
            }
        },
        fileProgress(file) {
            if (this.isExisting(file)) {
                return 100
            } else {
                return file.progress
            }
        },
        fileError(file) {
            if (this.isExisting(file)) {
                return false;
            } else {
                return file.error
            }
        },
        removeFile(file) {
            if (this.isExisting(file)) {
                let indexToRemove = this.existingImages.findIndex(searchFile => searchFile.id == file.id);
                this.existingImages.splice(indexToRemove, 1);
            } else {
                this.$refs.upload.remove(file);
            }
        },
        uploadToS3() {
            var vm = this
            var image = this.s3Queue.dequeue()
            var fileName = image.file.name
            var filePath = 'site_images/' + this.randomString(16) + this.getExtFromFilename(fileName, true)
            // var fileUrl = 'https://' + bucketRegion + '.amazonaws.com/site_images/' +  filePath;
            
            s3.upload({
                Key: filePath,
                Body: image.file,
                ACL: 'public-read',
            }, function(error, data) {
                image.response = { data }
                if (error) {
                    alert('Error Uploading')
                } else {
                    // alert('Successfully Uploaded')
                }
                if (vm.s3Queue.isFull()) {
                    vm.uploadToS3()
                } else {
                    vm.$emit('upload-complete', vm.imageData);
                }
            }).on('httpUploadProgress', function (progress) {
                var uploaded = parseInt((progress.loaded * 100) / progress.total);
                image.progress = uploaded
            });
        },
        randomString(length = 30) {
            return Math.random().toString(length * 2).substring(2, (length / 2) + 2) + Math.random().toString(length * 2).substring(2, (length / 2) + 2);
        },
        getExtFromFilename(filename, includeDot = false) {
            let re = /(?:\.([^.]+))?$/;
            let ext = re.exec(filename)[1] || ''
            if (includeDot && ext != null) {
                ext = '.' + ext
            }
            return ext
        },
        initSortable() {
            var vm = this
            var el = document.getElementById('files');
            this.fileSortable = Sortable.create(el, {
                onEnd(event) {
                    const oldIdx = event.oldIndex
                    const newIdx = event.newIndex
                    
                    vm.cleanSortOrder()
                    let movedImage = vm.combinedImages.find(image => image.sort_order == oldIdx)
                    vm.combinedImages.forEach(image => {
                        if (oldIdx > newIdx && image.sort_order < oldIdx && image.sort_order >= newIdx) {
                            image.sort_order++
                            console.log('plus', image.sort_order)
                        }
                        if (oldIdx < newIdx && image.sort_order > oldIdx && image.sort_order <= newIdx) {
                            console.log('minus', image.sort_order)
                            image.sort_order--
                        }
                    })
                    movedImage.sort_order = newIdx
                },
            });
        },
        cleanSortOrder() {
            // In case we some how get a non-sequential sort order
            let count = 0
            this.sortedImages.forEach(image => {
                image.sort_order = count++
            })
        },
    },
    mounted() {
        this.initSortable();
    },
};
</script>

<style scoped lang="scss">
.drop-active {
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    position: fixed;
    z-index: 9999;
    opacity: .6;
    text-align: center;
    background: #000;
}
.drop-active h3 {
    margin: -.5em 0 0;
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    -webkit-transform: translateY(-50%);
    -ms-transform: translateY(-50%);
    transform: translateY(-50%);
    font-size: 40px;
    color: #fff;
    padding: 0;
}
.image-gallery {
    border: 1px solid $lightest-gray;
    background: $off-white;
    border-radius: 4px;
    padding: 40px;
    margin: 10px 0;
    
    .files {
        position: relative;
        
        .file {
            height: 200px;
            background-size: cover;
            background-position: center center;
            
            .remove {
                position: absolute;
                top: -12px;
                right: -12px;
                color: black;
                background: none;
                border: none;
                font: inherit;
                cursor: pointer;
                outline: none;
                font-size: 18px;
            }
            &:hover {
                .remove {
                    color: #D83F4B;
                }
            }
            
            .file-progress {
                position: absolute;
                right: 0;
                bottom: 0;
                left: 0;
                height: 6px;
                background: white;
                
                .bar {
                    position: absolute;
                    top: 0;
                    bottom: 0px;
                    left: 0;
                    background: #333;
                }
            }
        }
    }
}
</style>